import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import userProfilePhoto from "./features/card/assets/images/user-profile.webp";
import userSignaturePhoto from "./features/card/assets/images/signature.webp";
import { AudioEffectsProvider } from "./hooks/use-audio-effects";
import Card from "./features/card";
import WalkieTalkie from "./features/walkie-talkie";
import GasStation from "./features/gas-station/components/GasStation";
import PilotJobUpgrade from "./features/pilot-job-upgrade/components/PilotJobUpgrade";
import BodyPartsStats from "./features/body-parts-stats/components/BodyPartsStats";
import MemoryMinigame from "./features/memory-minigame/index";
import ResurectMinigame from "./features/resurect-minigame/components/ResurectMinigame";
import PairsMinigame from "./features/pairs-minigame";
import Tuning from "./features/tuning/components/Tuning";
import ArrestDocument from "./features/arrest-document";
import WeddingDocument from "./features/wedding-document";
import ProposalDocument from "./features/proposal-document";
import { LoginPage } from "./features-v2/auth/pages/LoginPage";
import { RegisterPage } from "./features-v2/auth/pages/RegisterPage";
import { ReferralPage } from "./features-v2/auth/pages/ReferralPage";
import ClothingStore from "./features-v2/clothing-store";
import CharacterCustomization from "./features-v2/character-customization";
import MainMenu from "./features-v2/main-menu";
import EggPaintingMinigame from "./features-v2/egg-painting-minigame/index";
import { ElectricianPuzzle } from "./features-v2/electrician-game/index";
import EggsFightingGame from "./features-v2/eggs-fighting-game/index";
import { ModalProvider } from "./features-v2/modal/context/ModalContext";
import CarPanel from "./features-v2/car-panel";
import Speedometer from "./features-v2/speedometer/index";
import Inventory from "./features-v2/inventory";

export function App() {
  const user = {
    firstName: "Heyz",
    lastName: "Santos",
    birthday: "29.06.1999",
    gender: "men" as "men",
    expiresAt: "29.06.1999",
    licenseId: "E4-6-K207",
    nationality: "29.06.1999",
    signaturePhoto: userSignaturePhoto,
    profilePhoto: userProfilePhoto,
  };

  const routes = [
    {
      path: "/tuning",
      name: "Tuning",
      category: "ui",
      description:
        "Customize your vehicle's performance and visuals - engine, wheels, paint and more.",
      previewImage: "https://i.ibb.co/HLph5rxX/Screenshot-16.png",
      element: <Tuning />,
    },
    {
      path: "/pilot-job-upgrade",
      name: "Pilot Job Upgrade",
      category: "ui",
      description:
        "Spend your flight hours to unlock new pilot ranks and perks.",
      previewImage: "https://i.ibb.co/CZbSxnW/Screenshot-11.png",
      element: <PilotJobUpgrade />,
    },
    {
      path: "/gas-station",
      name: "Gas Station",
      category: "ui",
      description:
        "Refuel your vehicle at the pump - don't let the tank hit zero.",
      previewImage: "https://i.ibb.co/XqsLQt7/Screenshot-10.png",
      element: <GasStation />,
    },
    {
      path: "/walkie-talkie",
      name: "Walkie Talkie",
      category: "ui",
      description:
        "Compact radio UI for switching channels and broadcasting on the go.",
      previewImage: "https://i.ibb.co/7xBmSksQ/Screenshot-9.png",
      element: <WalkieTalkie />,
    },
    {
      path: "/body-parts-stats",
      name: "Body Parts Stats",
      category: "ui",
      description:
        "Visual health overview showing injury status per body region.",
      previewImage: "https://i.ibb.co/Wvqq9JgQ/Screenshot-12.png",
      element: <BodyPartsStats />,
    },
    {
      path: "/card",
      name: "Card",
      category: "ui",
      description:
        "Official diplomatic ID card with auto-blurred sensitive personal fields.",
      previewImage: "https://i.ibb.co/NdWTG5DL/Screenshot-8.png",
      element: (
        <Card
          title="Certificat Diplomatic"
          data={{
            firstName: user?.firstName,
            lastName: user?.lastName,
            birthday: user?.birthday,
            gender: user?.gender,
            expiresAt: user?.expiresAt,
            nationality: user?.nationality,
            licenseId: user?.licenseId,
            signaturePhoto: user?.signaturePhoto,
            profilePhoto: user?.profilePhoto,
          }}
          blur={["firstName", "lastName", "licenseId"]}
        />
      ),
    },
    {
      path: "/memory-minigame",
      name: "Memory Minigame",
      category: "games",
      description:
        "Flip and match hidden card pairs before the clock runs out.",
      previewImage: "https://i.ibb.co/6Rb8wgtW/Screenshot-13.png",
      element: <MemoryMinigame />,
    },
    {
      path: "/resurect-minigame",
      name: "Resurect Minigame",
      category: "games",
      description:
        "Beat the timed challenge to pull someone back from the brink of death.",
      previewImage: "https://i.ibb.co/hGw84Ty/Screenshot-14.png",
      element: <ResurectMinigame />,
    },
    {
      path: "/pairs-minigame",
      name: "Pairs Minigame",
      category: "games",
      description:
        "Race to clear the board by pairing matching cards correctly.",
      previewImage: "https://i.ibb.co/chhKrpx9/Screenshot-15.png",
      element: <PairsMinigame />,
    },
    {
      path: "/arrest-document",
      name: "Arrest Document",
      category: "ui",
      description:
        "Official police booking form - log charges, evidence, and detainee info.",
      previewImage: "https://i.ibb.co/LhB9sfnJ/Screenshot-3.png",
      element: <ArrestDocument />,
    },
    {
      path: "/wedding-document",
      name: "Wedding Document",
      category: "ui",
      description: "Formal marriage certificate issued and signed in-roleplay.",
      previewImage:
        "https://i.ibb.co/DHjDX37b/Screenshot-2026-02-19-at-22-51-51.png",
      element: <WeddingDocument />,
    },
    {
      path: "/proposal-document",
      name: "Proposal Document",
      category: "ui",
      description: "An in-game proposal letter to make the moment official.",
      previewImage:
        "https://i.ibb.co/7JJGB85T/Screenshot-2026-02-19-at-22-52-01.png",
      element: <ProposalDocument />,
    },
    {
      path: "/login",
      name: "Login Page",
      category: "ui",
      description:
        "Branded entry screen - authenticate before accessing the panel.",
      previewImage:
        "https://i.ibb.co/b5pLmHk2/Screenshot-2026-02-19-at-22-50-17.png",
      element: <LoginPage />,
    },
    {
      path: "/register",
      name: "Register Page",
      category: "ui",
      description:
        "Branded entry screen - create an account before accessing the panel.",
      previewImage: "https://i.ibb.co/3Yv8Zjm0/register.png",
      element: <RegisterPage />,
    },
    {
      path: "/referral",
      name: "Referall Page",
      category: "ui",
      description: "",
      previewImage: "https://i.imgur.com/QjNBXuV.png",
      element: <ReferralPage />,
    },
    {
      path: "/clothing-store",
      name: "Clothing store",
      category: "ui",
      description: "",
      previewImage: "https://i.imgur.com/eLcVIWW.jpeg",
      element: <ClothingStore />,
    },
    {
      path: "/character-customization",
      name: "Character Customization",
      category: "ui",
      description: "",
      previewImage: "https://i.ibb.co/KcwGFcyT/Screenshot-13.png",
      element: <CharacterCustomization />,
    },
    {
      path: "/main-menu",
      name: "Main Menu",
      category: "ui",
      description: "",
      previewImage: "https://i.ibb.co/MkbYx82h/Screenshot-12.png",
      element: <MainMenu />,
    },
    {
      path: "/egg-painting-minigame",
      name: "Egg painting minigame",
      category: "games",
      description: "",
      previewImage: "https://i.ibb.co/k2HWj3Kq/Screenshot-1.png",
      element: <EggPaintingMinigame />,
    },
    {
      path: "/electrician-game",
      name: "Electrician Puzzle",
      category: "games",
      description:
        "Rotate pipe tiles to restore the circuit — race against the timer before the power fails.",
      previewImage: "https://i.ibb.co/sv98NZ7k/Screenshot-2.png",
      element: (
        <ElectricianPuzzle
          autoStart={false}
          difficulty="easy"
          onWin={(s) => console.log("WIN", s)}
          onLose={(s) => console.log("LOSE", s)}
          onClose={() => window.history.back()}
        />
      ),
    },
    {
      path: "/eggs-fighting-game",
      name: "Eggs fighting game",
      element: <EggsFightingGame />,
      category: "games",
      previewImage: "https://i.ibb.co/Q7QRy8Sk/Screenshot-10.png",
    },
    {
      path: "/car-panel",
      name: "Car Panel & Radio",
      element: <CarPanel isOpen={true} onClose={() => {}} />,
      previewImage: "https://i.ibb.co/LdvGxQnG/Screenshot-3.png",
      category: "ui",
    },

    {
      path: "/inventory",
      name: "Inventory",
      category: "ui",
      description: "Player inventory with backpack, clothing, and nearby items.",
      previewImage: "https://i.ibb.co/MkbYx82h/Screenshot-12.png",
      element: <Inventory />,
    },
    {
      path: "/speedometer",
      name: "Speedometer HUD",
      category: "ui",
      description:
        "Vehicle HUD showing speed, RPM, fuel and status indicators.",
      previewImage: "https://i.ibb.co/4Zk6JJLT/Screenshot-9.png",
      element: (
        <div className="min-h-screen flex items-center justify-center bg-black">
          <Speedometer />
        </div>
      ),
    },
  ];

  const [selectedCategory, setSelectedCategory] = useState<
    "all" | "ui" | "games"
  >("all");

  const filteredRoutes =
    selectedCategory === "all"
      ? routes
      : routes.filter((r) => r.category === selectedCategory);

  return (
    <AudioEffectsProvider>
      <ModalProvider>
        <Router>
          <Routes>
            {routes.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}

            <Route
              path="*"
              element={
                <div className="relative min-h-screen overflow-hidden">
                  {/* ── Background ── */}
                  <img
                    src="https://i.ibb.co/DPSRDfKT/after-sunset-minimal-4k-zm.jpg"
                    className="absolute inset-0 w-full h-full object-cover z-0"
                  />
                  {/* Darkened + soft blur overlay */}
                  <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] z-[1]" />

                  {/* ── Page Content ── */}
                  <div className="relative z-[2] min-h-screen px-6 py-14 max-w-[1400px] mx-auto">
                    {/* Hero Header */}
                    <div className="mb-12 text-center">
                      <p className="text-blue-400 text-xs font-bold tracking-[0.35em] uppercase mb-3">
                        ✦ Component Library
                      </p>
                      <h1 className="text-5xl sm:text-6xl font-black text-white mb-4 tracking-tight leading-tight">
                        Route{" "}
                        <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                          Explorer
                        </span>
                      </h1>
                      <p className="text-gray-400 text-base max-w-md mx-auto">
                        Browse and preview every available route and demo in one
                        place. Developer: heyzbh
                      </p>
                    </div>

                    {/* Category Filter Pills */}
                    <div className="flex justify-center gap-3 mb-10 flex-wrap">
                      {(["all", "ui", "games"] as const).map((cat) => (
                        <button
                          key={cat}
                          onClick={() => setSelectedCategory(cat)}
                          className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 border ${
                            selectedCategory === cat
                              ? "bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/30 scale-105"
                              : "bg-white/10 border-white/10 text-gray-300 hover:bg-white/20 hover:border-white/20 backdrop-blur-sm"
                          }`}
                        >
                          {cat === "all" ? "🌐" : cat === "ui" ? "🎨" : "🎮"}
                          &nbsp;{cat.toUpperCase()}
                        </button>
                      ))}
                    </div>

                    {/* Route Count */}
                    <p className="text-gray-500 text-sm text-center mb-8">
                      Showing{" "}
                      <span className="text-gray-300 font-semibold">
                        {filteredRoutes.length}
                      </span>{" "}
                      route{filteredRoutes.length !== 1 ? "s" : ""}
                    </p>

                    {/* Route Cards — staggered grid */}
                    <motion.div
                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                      initial="hidden"
                      animate="visible"
                      variants={{
                        visible: {
                          transition: { staggerChildren: 0.07 },
                        },
                      }}
                    >
                      {filteredRoutes.map((r) => (
                        <motion.div
                          key={r.path}
                          variants={{
                            hidden: { opacity: 0, y: 28 },
                            visible: {
                              opacity: 1,
                              y: 0,
                              transition: { duration: 0.4, ease: "easeOut" },
                            },
                          }}
                          whileHover={{ y: -6 }}
                          whileTap={{ scale: 0.97 }}
                          className="group relative flex flex-col bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl overflow-hidden shadow-xl hover:border-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300"
                        >
                          {/* Card Image */}
                          <div className="relative h-48 overflow-hidden flex-shrink-0">
                            <img
                              src={r.previewImage}
                              alt={r.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ease-in-out"
                            />
                            {/* Gradient scrim for readability */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                            {/* Category Badge */}
                            <span
                              className={`absolute top-3 right-3 px-3 py-1 text-[11px] font-extrabold rounded-full tracking-widest uppercase shadow-md ${
                                r.category === "games"
                                  ? "bg-violet-500/90 text-white"
                                  : r.category === "ui"
                                    ? "bg-emerald-500/90 text-white"
                                    : "bg-blue-500/90 text-white"
                              }`}
                            >
                              {r.category}
                            </span>
                          </div>

                          {/* Card Body */}
                          <div className="flex flex-col flex-1 p-5 gap-3">
                            <h3 className="text-white font-bold text-lg leading-snug">
                              {r.name}
                            </h3>
                            <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 flex-1">
                              {r.description}
                            </p>
                            <Link
                              to={r.path}
                              className="mt-auto self-start inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:gap-2.5"
                            >
                              View Demo
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.5}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                              </svg>
                            </Link>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>
                </div>
              }
            />
          </Routes>
        </Router>
      </ModalProvider>
    </AudioEffectsProvider>
  );
}

export default App;
